import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useValidateZipCode } from '../../../hooks/useValidateZipCode';
import { GEO_ZIP_URL_PARAM } from '../../../constants/environment';
import { selectZipCode } from '../../../reducers/zipCodeReducer';
import {
  ZipInputWrapper,
  InputComponent,
  InputWrapper,
  TextInputField,
  DeleteInputIcon,
  Description,
  BackToNational,
  StyledCTA,
} from './ZipInput.styles';
import LocationIcon from '../../../assets/icons/LocationIcon';
import CancelIcon from '../../../assets/icons/CancelIcon';
import { COLORS } from '../../../styles/variables';
import BackIcon from '../../../assets/icons/BackIcon';
import { clearAllFilters, selectFilters } from '../../../reducers/filtersReducer';
import { filterClearAll, filterCurrentState } from '../../../utils/analyticsEventListeners';
import { INTIAL_FILTERS } from '../../../constants/initialFilters';

const ZipInput: React.FC = () => {
  const { t } = useTranslation();

  const { confirmedZipcode } = useSelector(selectZipCode);
  const { confirmedFilters } = useSelector(selectFilters);

  const isNatPage = confirmedZipcode === '';

  const [inputValue, setInputValue] = useState('');

  const dispatch = useDispatch();

  const { validatingZip, setAlreadyValidZip, zipValidationLoading } = useValidateZipCode();

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const clearInput = () => {
    setInputValue('');
  };

  // Effect to set autimatically ValidZip code from session storage (this ZIP was already validated before)
  useEffect(() => {
    if (confirmedZipcode) {
      setInputValue(confirmedZipcode);
    }
  }, [confirmedZipcode, setAlreadyValidZip]);

  useEffect(() => {
    if (GEO_ZIP_URL_PARAM && !confirmedZipcode) setInputValue(GEO_ZIP_URL_PARAM);
  }, [confirmedZipcode]);

  const handleClearZipCta = () => {
    setAlreadyValidZip('');
    setInputValue('');
    filterClearAll(confirmedFilters);
    dispatch(clearAllFilters());
    filterCurrentState(INTIAL_FILTERS);
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      validatingZip(inputValue);
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    validatingZip(inputValue);
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  };

  return (
    <ZipInputWrapper onSubmit={handleSubmit} data-cy="zip-input-form">
      <InputComponent>
        <InputWrapper data-cy="zip-input-wrapper">
          <LocationIcon color={COLORS.black} />
          <TextInputField
            placeholder={t('promo.filters.zipSearch.placeholder')}
            value={inputValue}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
          />
          {inputValue.length ? (
            <DeleteInputIcon onClick={clearInput}>
              <CancelIcon />
            </DeleteInputIcon>
          ) : null}
        </InputWrapper>
        {isNatPage ? (
          <Description>{t('promo.filters.zipSearch.description')}</Description>
        ) : (
          <BackToNational onClick={handleClearZipCta}>
            <BackIcon />
            {t('promo.filters.zipSearch.returnNational')}
          </BackToNational>
        )}
      </InputComponent>
      <StyledCTA
        variant="secondary"
        size="sm"
        aria-label="Submit"
        isLoading={zipValidationLoading}
        type="submit"
        data-testid="find-my-offers-cta"
        onClick={() => {}}
      >
        Submit
      </StyledCTA>
    </ZipInputWrapper>
  );
};

export default ZipInput;
